import type { Actor } from '../../battle/types';
import type { ArenaInteractionsHandler } from '../interactions';

// Object id used in arena_attach / arena_release events for cannonballs stuck
// in the pit walls. Shared with the renderer-side bundle (playArenaEvent).
export const CANNONBALL_OBJECT_ID = 'cannonball';

type EmbeddedBall = {
  sourceSkillId: string;
  // Damage to deal when the ball is dislodged. Defaults to the amount the
  // original block absorbed - blocking delays the hit but does not erase it.
  pendingDamage: number;
  // Carried from the source skill so the delayed blast still launches the
  // victim if the skill was a launcher.
  launches: boolean;
};

type ThePitState = {
  // One ball per side at most. Second block on the same side replaces silently.
  embeddedBalls: Record<Actor, EmbeddedBall | null>;
};

export const THE_PIT_INTERACTIONS: ArenaInteractionsHandler<ThePitState> = {
  id: 'the_pit',
  initialState: () => ({ embeddedBalls: { p1: null, p2: null } }),

  onAttackResolved(ctx, state) {
    // Cannon shot blocked -> ball lodges in the defender's wall above them.
    if (ctx.attackSkill?.id === 'cannon' && ctx.reaction === 'block') {
      state.embeddedBalls[ctx.defender] = {
        sourceSkillId: ctx.attackSkill.id,
        pendingDamage: Math.floor(ctx.reducedDamage),
        launches: ctx.attackSkill.launches ?? false,
      };
      ctx.emit({
        type: 'arena_attach',
        side: ctx.defender,
        objectId: CANNONBALL_OBJECT_ID,
        sourceSkillId: ctx.attackSkill.id,
      });
    }

    // Earthshake-tagged attack -> every embedded ball falls and explodes.
    // Iteration order p1 -> p2 is fixed so event sequences stay deterministic.
    // All explosions are SIMULTANEOUS - collect the hits and deal them in one
    // batch so the renderer plays both reactions at once instead of serially.
    if (ctx.attackSkill?.effects?.includes('earthshake')) {
      const hits: { target: Actor; amount: number; launches: boolean }[] = [];
      for (const side of ['p1', 'p2'] as const) {
        const ball = state.embeddedBalls[side];
        if (!ball) continue;
        state.embeddedBalls[side] = null;
        ctx.emit({ type: 'arena_release', side, objectId: CANNONBALL_OBJECT_ID });
        // Skip damage if the target is already dead (e.g. earthshake itself
        // killed them) so we don't pile damage onto a 0-HP combatant. HP is
        // read pre-explosion for every side (the blasts are simultaneous).
        if (ball.pendingDamage > 0 && ctx.getHP(side) > 0) {
          hits.push({ target: side, amount: ball.pendingDamage, launches: ball.launches });
        }
      }
      if (hits.length > 0) ctx.dealDamageBatch(hits);
    }
  },
};
